import type {
  FetchedTradingSecurity,
  FetchedStock,
  FrontendStockFormat,
  FetchedMutualFund,
  Fiat,
} from "~/types/global.types";
import { fallbackFiat } from "./fallbacks";
import { convertCurrency } from "~/calculations/genericCalculations/convertCurrency";
import { getCurrency } from "./sharesCurrencyExhange";

export const formatStocksFrontend = (
  stocks: (FetchedStock | FetchedTradingSecurity | FetchedMutualFund)[],
  fiat: Fiat[]
): FrontendStockFormat[] => {
  if (!stocks || stocks?.length === 0) return [];

  const baseCurrency =
    fiat.find((currency) => currency.value === "ILS") || fallbackFiat;

  const worldApiStocks = stocks.filter(
    (stock) => (stock as FetchedStock).type
  ) as FetchedStock[];

  const taseApiStocks = stocks.filter(
    (stock) => (stock as FetchedTradingSecurity).base_price
  ) as FetchedTradingSecurity[];

  const formattedWorldStocks = formatWorldStocks(
    worldApiStocks,
    taseApiStocks,
    baseCurrency,
    fiat
  );

  const formattedTaseStocks = formatTaseStocks(taseApiStocks, baseCurrency, fiat);

  return [...formattedTaseStocks, ...formattedWorldStocks];
};

/*
  * Filter world stocks that are already in the tase stocks
  * This is because the world stocks api returns stocks that are also in the tase api
  * and we don't want to show them twice
  * for example: "TEVA" is in both apis

*/
const filterWorldStocksDuplicates = (
  fetchedWorldStocks: FetchedStock[],
  fetchedTaseStocks: FetchedTradingSecurity[]
): FetchedStock[] => {
  // create a symbol -> tase stock map
  const taseStocksMap: Map<string, FetchedTradingSecurity> =
    fetchedTaseStocks.reduce((stocksMap, taseStock) => {
      stocksMap.set(taseStock.symbol, taseStock);
      return stocksMap;
    }, new Map<string, FetchedTradingSecurity>());

  return fetchedWorldStocks.filter((stock) => !taseStocksMap.has(stock.symbol));
};

const formatWorldStocks = (
  fetchedWorldStocks: FetchedStock[],
  fetchedTaseStocks: FetchedTradingSecurity[],
  baseCurrency: Fiat,
  fiat: Fiat[]
): FrontendStockFormat[] => {
  const filteredWorldStocks = filterWorldStocksDuplicates(
    fetchedWorldStocks,
    fetchedTaseStocks
  );

  const formattedWorldStocks: FrontendStockFormat[] = [];
  filteredWorldStocks.forEach((stock) => {
    const formattedStock = formatWorldStock(stock, baseCurrency, fiat);
    //it could be undefined if there is no exchange currency
    //by the currencies table
    //if there's no exchange currency, we don't need this share
    if (formattedStock) formattedWorldStocks.push(formattedStock);
  });
  return formattedWorldStocks;
};

const formatWorldStock = (
  worldStock: FetchedStock,
  baseCurrency: Fiat,
  fiat: Fiat[]
): FrontendStockFormat | undefined => {
  const currency = getCurrency(worldStock, fiat);
  if (!currency) return undefined;
  return {
    name: worldStock.symbol,
    fullName: worldStock.name,
    value: worldStock.symbol,
    currency: currency,
    worthInBaseCurrency: convertCurrency(
      worldStock.worth,
      baseCurrency,
      currency
    ),
    worth: worldStock.worth,
    type: worldStock.type,
    market: worldStock.exchange_market_short,
  };
};

const formatTaseStocks = (
  fetchedTaseStocks: FetchedTradingSecurity[],
  taseCurrency: Fiat,
  fiat: Fiat[]
): FrontendStockFormat[] => {
  return fetchedTaseStocks
    .map((taseStock) => formatTaseStock(taseStock, taseCurrency,fiat))
    .flat();
};

const formatTaseStock = (
  taseStock: FetchedTradingSecurity,
  taseCurrency: Fiat,
  fiat: Fiat[]
): FrontendStockFormat[] => {
  const currency = getCurrency(taseStock, fiat) || taseCurrency;
  const formattedTaseStocks: FrontendStockFormat[] = [
    // {
    //   name: taseStock.symbol,
    //   fullName: taseStock.symbol,
    //   value: taseStock.symbol,
    //   worth: taseStock.base_price,
    //   currency: currency,
    //   worthInBaseCurrency: convertCurrency(
    //     taseStock.base_price,
    //     taseCurrency,
    //     currency
    //   ),
    //   type: taseStock.market_type,
    //   market: taseStock.exchange_market_short,
    // },
    {
      name: taseStock.symbol,
      fullName: taseStock.symbol,
      value: taseStock.security_id,
      worth: taseStock.base_price,
      currency: currency,
      worthInBaseCurrency: convertCurrency(
        taseStock.base_price,
        taseCurrency,
        currency
      ),
      type: taseStock.market_type,
      market: taseStock.exchange_market_short,
    },
  ];

  return formattedTaseStocks;
};
